import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { TableColumn } from '../../models/table-column.model';
import { ExportService, IExportBlock, IExportColumn } from '../../services/export.service';
import { NpsService } from '../../services/nps.service';
import { ViewportService } from '../../services/viewport.service';

/**
 * Tabla equivalente a los DataTables del tablero original: botones de
 * exportacion, buscador, selector de longitud, ordenamiento, paginacion,
 * semaforo de color por umbral y filas expandibles.
 */
@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss',
  standalone: false,
})
export class DataTableComponent implements OnChanges, AfterViewInit {

  @Input({ required: true }) public columns: Array<TableColumn> = [];
  @Input({ required: true }) public rows: Array<any> = [];

  /** Botones morados de exportacion. [] los oculta. */
  @Input() public exportButtons: Array<string> = ['Copiar', 'CSV', 'PDF'];

  /** Titulo del bloque exportado y base del nombre del archivo. */
  @Input() public exportTitle = 'Tabla';

  /** Contexto que se escribe en la cabecera del archivo (periodo y filtros). */
  @Input() public exportPeriod?: string;
  @Input() public exportFilters?: string;
  @Input() public searchable = true;
  /** Muestra "Mostrar N registros". */
  @Input() public showLengthSelect = false;
  /** Muestra "Mostrando registros del X al Y de un total de Z". */
  @Input() public showInfo = false;
  @Input() public pageSize = 10;
  @Input() public paginated = true;
  /** Habilita el control ">" de fila expandible. */
  @Input() public expandable = false;
  /** Plantilla del detalle de la fila expandida. */
  @Input() public detailTemplate?: TemplateRef<any>;

  /** Emite la fila cuando se hace click en una celda de tipo icono. */
  @Output() public iconAction = new EventEmitter<{ row: any; column: TableColumn }>();

  @ViewChild('scrollBox') private scrollBox?: ElementRef<HTMLElement>;
  @ViewChild('scroller') private scroller?: ElementRef<HTMLElement>;

  public search = '';
  public page = 1;
  public sortKey?: string;
  public sortAsc = true;
  public expanded = new Set<number>();

  public readonly lengthOptions = [10, 25, 50, 0];

  /** Confirmacion efimera del boton «Copiar»; el portapapeles no se ve. */
  public copiado: '' | 'ok' | 'error' = '';

  private readonly exportService: ExportService = inject(ExportService);

  /** Puede haber varias tablas por pantalla: los ids de los controles deben ser unicos. */
  private static instances = 0;
  private readonly uid: number = ++DataTableComponent.instances;
  public readonly searchId: string = `dt-search-${this.uid}`;
  public readonly lengthId: string = `dt-length-${this.uid}`;

  constructor(
    private npsService: NpsService,
    private viewport: ViewportService,
  ) {}

  public ngOnChanges(): void {
    this.page = 1;
    this.expanded.clear();
    this.scheduleScrollHint();
  }

  public ngAfterViewInit(): void {
    this.scheduleScrollHint();
  }

  /* ------------------------------------------------------------------ */
  /*                          ANCHOR derivados                          */
  /* ------------------------------------------------------------------ */

  public get filtered(): Array<any> {
    const term = this.search.trim().toLowerCase();

    let data = !term
      ? [...this.rows]
      : this.rows.filter((row) =>
          this.columns.some((column) =>
            String(row[column.key] ?? '').toLowerCase().includes(term)));

    if (this.sortKey) {
      const key = this.sortKey;
      data.sort((a, b) => {
        const va = a[key];
        const vb = b[key];
        const numeric = typeof va === 'number' && typeof vb === 'number';
        const result = numeric
          ? (va as number) - (vb as number)
          : String(va ?? '').localeCompare(String(vb ?? ''), 'es');
        return this.sortAsc ? result : -result;
      });
    }

    return data;
  }

  public get total(): number { return this.filtered.length; }

  public get effectivePageSize(): number {
    return this.pageSize === 0 || !this.paginated ? this.total || 1 : this.pageSize;
  }

  public get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.effectivePageSize));
  }

  public get paged(): Array<any> {
    if (!this.paginated && this.pageSize === 0) return this.filtered;
    const start = (this.page - 1) * this.effectivePageSize;
    return this.filtered.slice(start, start + this.effectivePageSize);
  }

  public get infoText(): string {
    if (!this.total) return 'No hay registros para mostrar';
    const start = (this.page - 1) * this.effectivePageSize + 1;
    const end = Math.min(start + this.effectivePageSize - 1, this.total);
    return `Mostrando registros del ${start} al ${end} de un total de ${this.total} registros`;
  }

  /**
   * En `xs` la ventana numerada se cambia por «Anterior · 3 / 12 · Siguiente».
   *
   * Con objetivos tactiles de 44px, «Anterior» y «Siguiente» ya se comen unos
   * 190px de los ~300 utiles de la tarjeta en un telefono de 375px: no quedan
   * ni dos numeros. Y una ventana recortada tampoco resuelve nada, porque para
   * saltar de la pagina 3 a la 12 sigue haciendo falta la ultima, que es
   * justamente la que se cae. El indicador dice donde esta uno y los dos
   * botones bastan para moverse en una tabla que ahi se lee de diez en diez.
   */
  public get compactPagination(): boolean {
    return this.viewport.breakpoint() === 'xs';
  }

  /** Ventana de paginas estilo DataTables: 1 2 3 4 5 … N */
  public get pageWindow(): Array<number | '…'> {
    const last = this.totalPages;
    if (last <= 6) return Array.from({ length: last }, (_, i) => i + 1);

    const window: Array<number | '…'> = [];
    const from = Math.max(1, Math.min(this.page - 2, last - 5));
    for (let i = from; i < from + 5; i++) window.push(i);
    if (from + 5 < last) window.push('…');
    if (!window.includes(last)) window.push(last);
    return window;
  }

  public get colspan(): number {
    return this.columns.length + (this.expandable ? 1 : 0);
  }

  public get isFiltered(): boolean {
    return this.search.trim().length > 0;
  }

  /** Texto de apoyo del estado vacio: cambia si el hueco lo provoco la busqueda. */
  public get emptyHint(): string {
    return this.isFiltered
      ? `No encontramos registros que coincidan con «${this.search.trim()}».`
      : 'Todavía no hay información para mostrar.';
  }

  /* ------------------------------------------------------------------ */
  /*                          ANCHOR acciones                           */
  /* ------------------------------------------------------------------ */

  public toggleSort(column: TableColumn): void {
    if (column.sortable === false) return;
    if (this.sortKey === column.key) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortKey = column.key;
      this.sortAsc = true;
    }
    this.page = 1;
  }

  public onSearch(value: string): void {
    this.search = value;
    this.page = 1;
    this.scheduleScrollHint();
  }

  public onLengthChange(value: string): void {
    this.pageSize = Number(value);
    this.page = 1;
    this.scheduleScrollHint();
  }

  public goTo(page: number | '…'): void {
    if (page === '…') return;
    this.page = Math.min(Math.max(1, page), this.totalPages);
  }

  public toggleRow(index: number): void {
    if (this.expanded.has(index)) this.expanded.delete(index);
    else this.expanded.add(index);
  }

  public isExpanded(index: number): boolean {
    return this.expanded.has(index);
  }

  public onIconClick(row: any, column: TableColumn): void {
    this.iconAction.emit({ row, column });
  }

  /* ------------------------------------------------------------------ */
  /*                        ANCHOR exportacion                          */
  /* ------------------------------------------------------------------ */

  /**
   * La tabla se exporta a si misma: sabe sus columnas y sus filas, y ninguna
   * pagina tiene que repetir esa lista para los quince `<app-data-table>` del
   * tablero.
   *
   * Se exporta **lo filtrado y ordenado, completo** —no solo la pagina
   * visible—, que es lo que hace DataTables y lo que espera quien busca "las
   * 145 sucursales" y no "las diez que alcanzo a ver".
   */
  public onExport(boton: string): void {
    const bloque = this.exportBlock();
    const archivo = this.exportService.nombreArchivo(this.exportTitle);

    switch (boton.toLowerCase()) {
      case 'copiar':
        this.exportService.copiar([bloque]).then((ok) => this.avisarCopia(ok));
        return;

      case 'csv':
        this.exportService.csv([bloque], archivo, this.exportMeta());
        return;

      case 'excel':
        this.exportService.xlsx([bloque], archivo);
        return;

      case 'pdf':
      case 'print':
        this.exportService.imprimir([bloque], this.exportMeta());
        return;

      default:
        return;
    }
  }

  /**
   * Las columnas de icono (HISTORICO, DETALLE) se quedan fuera: son controles,
   * su celda no lleva dato y en un CSV solo produce una columna vacia.
   */
  private exportBlock(): IExportBlock {
    const columnas: Array<IExportColumn> = this.columns
      .filter((column) => column.cellStyle !== 'icon')
      .map((column) => ({ key: column.key, title: column.title, suffix: column.suffix }));

    return { titulo: this.exportTitle, columnas, filas: this.filtered };
  }

  private exportMeta() {
    const busqueda = this.isFiltered ? `búsqueda «${this.search.trim()}»` : '';
    const filtros = [this.exportFilters, busqueda].filter(Boolean).join(' · ');

    return { titulo: this.exportTitle, periodo: this.exportPeriod, filtros };
  }

  private avisarCopia(ok: boolean): void {
    this.copiado = ok ? 'ok' : 'error';
    setTimeout(() => (this.copiado = ''), 2200);
  }

  public get copiadoTexto(): string {
    if (this.copiado === 'ok') {
      return `${this.total} ${this.total === 1 ? 'registro copiado' : 'registros copiados'}`;
    }

    return this.copiado === 'error' ? 'No se pudo copiar' : '';
  }

  /* ------------------------------------------------------------------ */
  /*                      ANCHOR aviso de scroll                        */
  /* ------------------------------------------------------------------ */

  public onScroll(): void {
    this.updateScrollHint();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.updateScrollHint();
  }

  private scheduleScrollHint(): void {
    if (typeof requestAnimationFrame !== 'function') return;
    requestAnimationFrame(() => this.updateScrollHint());
  }

  /**
   * Enciende el degradado del borde derecho mientras quede tabla por descubrir.
   * Se marca directamente sobre el DOM: es un adorno y no vale la pena disparar
   * un ciclo de deteccion de cambios por cada pixel de scroll.
   */
  private updateScrollHint(): void {
    const scroller = this.scroller?.nativeElement;
    const box = this.scrollBox?.nativeElement;
    if (!scroller || !box) return;

    const pending = scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft;
    box.classList.toggle('dt-scroll--fade', pending > 4);
  }

  /* ------------------------------------------------------------------ */
  /*                           ANCHOR celdas                            */
  /* ------------------------------------------------------------------ */

  public headerClass(column: TableColumn): string {
    const map: Record<string, string> = {
      detractor: 'th-detractor',
      pasivo: 'th-pasivo',
      promotor: 'th-promotor',
      teal: 'th-teal',
    };
    return map[column.headerStyle ?? 'default'] ?? '';
  }

  public cellClass(column: TableColumn, row: any): string {
    if (column.cellStyle === 'nps') {
      return this.npsService.colorClass(this.numericValue(row[column.key]));
    }
    if (column.cellStyle === 'center' || column.cellStyle === 'icon') return 'z-cell-center';
    return '';
  }

  public cellText(column: TableColumn, row: any): string {
    const value = row[column.key];
    if (value === null || value === undefined || value === '') return '';
    return `${value}${column.suffix ?? ''}`;
  }

  private numericValue(value: any): number {
    if (typeof value === 'number') return value;
    const parsed = parseFloat(String(value).replace('%', '').trim());
    return isNaN(parsed) ? NaN : parsed;
  }

  /* ------------------------------------------------------------------ */
  /*                       ANCHOR accesibilidad                         */
  /* ------------------------------------------------------------------ */

  /** aria-sort del encabezado: null cuando la columna no es ordenable. */
  public ariaSort(column: TableColumn): string | null {
    if (column.sortable === false) return null;
    if (this.sortKey !== column.key) return 'none';
    return this.sortAsc ? 'ascending' : 'descending';
  }

  public toggleLabel(index: number): string {
    return this.isExpanded(index)
      ? 'Ocultar el detalle de la fila'
      : 'Ver el detalle de la fila';
  }

  public iconLabel(column: TableColumn): string {
    return `Ver ${column.title.toLowerCase()}`;
  }
}
